import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  Search, X, Star,
  CalendarCheck, Clock, Car, Coffee, Waves, BedDouble, DoorOpen, Wifi,
  PawPrint, Accessibility, Users, Briefcase, CalendarDays, WashingMachine,
  MapPin, ShieldCheck, CreditCard, HelpCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import { PageHero } from "../components/PageHero";
import { CtaBlock } from "../components/CtaBlock";
import { buildFaqLd } from "../components/FaqAccordion";
import { buildBreadcrumbLd } from "../components/Breadcrumbs";
import { useSeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category metadata: label + icon + display order.
const CATEGORY_META = {
  reservations:   { label: "Reservations & Booking",   icon: CalendarCheck },
  "check-in-out": { label: "Check-in & Check-out",     icon: Clock },
  parking:        { label: "Parking",                  icon: Car },
  breakfast:      { label: "Breakfast",                icon: Coffee },
  pool:           { label: "Indoor Pool",              icon: Waves },
  rooms:          { label: "Rooms",                    icon: BedDouble },
  corridors:      { label: "Interior vs Exterior",     icon: DoorOpen },
  wifi:           { label: "Wi-Fi",                    icon: Wifi },
  pets:           { label: "Pets",                     icon: PawPrint },
  accessibility:  { label: "Accessibility",            icon: Accessibility },
  groups:         { label: "Groups & Blocks",          icon: Users },
  business:       { label: "Business",                 icon: Briefcase },
  "long-stay":    { label: "Long Stays",               icon: CalendarDays },
  laundry:        { label: "Laundry",                  icon: WashingMachine },
  local:          { label: "Local Area",               icon: MapPin },
  policies:       { label: "Policies",                 icon: ShieldCheck },
  payments:       { label: "Payments",                 icon: CreditCard },
};

// Curated "Most asked" — 6 real questions guests ask the front desk.
const POPULAR_KEYS = [
  "book a room",
  "check-in",
  "pool",
  "pets",
  "breakfast",
  "parking",
];

// One expandable Q&A row.
function FaqRow({ faq, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-tan/50 last:border-b-0" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left flex items-start gap-3 py-3 md:py-3.5 group outline-none focus-visible:bg-cream-100/60"
        data-testid={`${testId}-toggle`}
      >
        <span className="flex-1 font-display text-forest-900 text-[0.98rem] md:text-base leading-snug group-hover:text-forest transition-colors">
          {faq.q}
        </span>
        <span className="mt-0.5 text-forest/70 group-hover:text-brass transition-colors shrink-0">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {open && (
        <div className="pb-4 pr-6 text-ink/80 leading-relaxed text-[0.9rem] md:text-[0.94rem]" data-testid={`${testId}-answer`}>
          {faq.a}
        </div>
      )}
    </div>
  );
}

// One category section: header + two-column grid + "show all" toggle.
function CategorySection({ catKey, faqs, expandAll }) {
  const meta = CATEGORY_META[catKey] || { label: catKey, icon: HelpCircle };
  const Icon = meta.icon;
  const [showAll, setShowAll] = useState(false);
  const INITIAL = 8;
  const visible = showAll || expandAll ? faqs : faqs.slice(0, INITIAL);
  const hasMore = faqs.length > INITIAL && !showAll && !expandAll;
  // Split into two independent columns so expanding one doesn't reflow the other.
  const leftCol = visible.filter((_, i) => i % 2 === 0);
  const rightCol = visible.filter((_, i) => i % 2 === 1);

  return (
    <section
      id={`cat-${catKey}`}
      data-testid={`faq-category-${catKey}`}
      className="scroll-mt-40"
    >
      <div className="flex items-center gap-3 mb-4 md:mb-5">
        <span className="w-9 h-9 grid place-items-center bg-brass/10 border border-brass/30 text-brass shrink-0">
          <Icon size={16} strokeWidth={1.5} />
        </span>
        <div>
          <h2 className="font-display text-lg md:text-xl text-forest-900 leading-tight">
            {meta.label}
          </h2>
          <p className="text-[0.68rem] uppercase tracking-[0.14em] text-ink/55 mt-0.5">
            {faqs.length} {faqs.length === 1 ? "question" : "questions"}
          </p>
        </div>
      </div>
      <div className="bg-cream-50 border border-tan/60 px-4 md:px-5">
        {faqs.length === 1 ? (
          <FaqRow faq={faqs[0]} testId={`faq-${catKey}-0`} />
        ) : (
          <div className="grid md:grid-cols-2 md:gap-x-8 items-start">
            <div className="md:border-r md:border-tan/50 md:pr-2">
              {leftCol.map((f, i) => (
                <FaqRow key={`${catKey}-l-${i}`} faq={f} testId={`faq-${catKey}-l-${i}`} />
              ))}
            </div>
            <div className="md:pl-2">
              {rightCol.map((f, i) => (
                <FaqRow key={`${catKey}-r-${i}`} faq={f} testId={`faq-${catKey}-r-${i}`} />
              ))}
            </div>
          </div>
        )}
      </div>
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-3 inline-flex items-center gap-2 text-sm text-forest-900 brass-underline"
          data-testid={`faq-showall-${catKey}`}
        >
          Show all {faqs.length} in {meta.label} <ChevronDown size={14} />
        </button>
      )}
    </section>
  );
}

export default function FaqPage() {
  const [faqs, setFaqs] = useState([]);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/faqs/full`).then((r) => {
      if (!alive) return;
      setFaqs(r.data.faqs || []);
      setCategoryOrder(r.data.categories || []);
    });
    return () => { alive = false; };
  }, []);

  // Curated "Most Asked" strip — matches on the natural-language keys above.
  const popular = useMemo(() => {
    if (!faqs.length) return [];
    const found = [];
    for (const key of POPULAR_KEYS) {
      const hit = faqs.find(
        (f) => f.q.toLowerCase().includes(key) && !found.includes(f),
      );
      if (hit) found.push(hit);
    }
    return found;
  }, [faqs]);

  // Group by category, preserving server-provided category order.
  const groupedAll = useMemo(() => {
    const out = {};
    for (const c of categoryOrder) out[c] = [];
    for (const f of faqs) {
      const c = f.category || "other";
      if (!out[c]) out[c] = [];
      out[c].push(f);
    }
    return out;
  }, [faqs, categoryOrder]);

  // Normalize both query and content so hyphenated variants match:
  // "checkout" ⇄ "check out" ⇄ "check-out", "wifi" ⇄ "wi-fi", etc.
  const normalize = (s) => (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const compact = (s) => normalize(s).replace(/\s+/g, "");

  // Synonym map — expands common guest-search phrases into corpus vocabulary.
  // Keys and values are matched after normalization, so hyphens/case don't matter.
  // A query "pet friendly" expands to also try "pets", "pet fee", "dog", "cat"
  // AGAINST the FAQ text — so if any of those words appear in a Q/A, it hits.
  const SYNONYMS = {
    "pet friendly": ["pets", "pet fee", "dog", "cat"],
    "pets allowed": ["pets", "pet fee", "dog", "cat"],
    "dog friendly": ["pets", "dog", "cat"],
    "cat friendly": ["pets", "dog", "cat"],
    "spa": ["jacuzzi"],
    "hot tub": ["jacuzzi suite", "jacuzzi tub"],
    "whirlpool": ["jacuzzi suite", "jacuzzi tub"],
    "sauna": ["jacuzzi suite"],
    "wifi": ["internet", "wireless"],
    "wi fi": ["internet", "wireless"],
    "internet": ["wifi", "wireless"],
    "parking": ["truck parking", "lot", "park"],
    "truck": ["truck parking", "trucker", "driver"],
    "trucker": ["truck parking", "driver"],
    "breakfast": ["waffle", "coffee", "continental"],
    "waffle": ["breakfast", "continental"],
    "handicap": ["accessible", "ada"],
    "wheelchair": ["accessible", "ada", "roll-in"],
    "disabled": ["accessible", "ada"],
    "ada": ["accessible", "roll-in"],
    "swim": ["pool", "indoor pool"],
    "pool hours": ["pool"],
    "meeting": ["conference", "event", "meetings"],
    "conference": ["meeting", "event", "meetings"],
    "wedding": ["reception", "event", "group"],
    "shower": ["event", "celebration", "reception"],
    "reception": ["event", "wedding", "meeting"],
    "anniversary": ["jacuzzi suite", "romance", "celebrate"],
    "honeymoon": ["jacuzzi suite", "romance"],
    "romantic": ["jacuzzi suite", "romance"],
    "check out": ["checkout", "leave", "late checkout"],
    "checkout": ["check out", "leave"],
    "check in": ["checkin", "arrive"],
    "checkin": ["check in", "arrive"],
    "late arrival": ["24 hour", "front desk"],
    "kids": ["family", "child", "children"],
    "child": ["family", "kids", "children"],
    "family": ["kids", "child", "children"],
    "no smoking": ["non smoking", "smoke free"],
    "smoke free": ["non smoking", "no smoking"],
    "cancel": ["cancellation", "refund"],
    "cancellation": ["cancel", "refund"],
    "refund": ["cancel", "cancellation"],
    "cost": ["rate", "price", "call"],
    "price": ["rate", "cost", "call"],
    "rate": ["price", "cost", "call"],
  };

  // Expand a raw query into a list of normalized terms.
  // Original + any synonym-map values whose key is contained in the query.
  const expandQuery = (raw) => {
    const q = normalize(raw);
    if (!q) return [];
    const terms = new Set([q]);
    for (const [key, alts] of Object.entries(SYNONYMS)) {
      const nk = normalize(key);
      if (q.includes(nk) || compact(raw).includes(compact(key))) {
        for (const a of alts) terms.add(normalize(a));
      }
    }
    return Array.from(terms);
  };

  // Match a single expansion term against the FAQ text.
  // Rule: compound queries (with a space) use compact-substring matching so
  // "check out" matches "checkout"; single tokens require a word boundary in
  // the normalized text so "spa" doesn't match "space", "spacious", etc.
  // Very short tokens (< 4 chars) always use word-boundary to avoid noise.
  const matchTerm = (term, hay, hayc) => {
    const t = normalize(term);
    if (!t) return false;
    if (t.includes(" ")) {
      // Compound: allow hyphen/space variance via compact form.
      const tc = t.replace(/\s+/g, "");
      return hayc.includes(tc);
    }
    // Single token — build a word-boundary regex on the normalized haystack.
    // Escape any accidental regex metacharacters (shouldn't be any after normalize).
    const safe = t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\b${safe}\\b`);
    if (re.test(hay)) return true;
    // Also allow compact substring for tokens >= 4 chars ("wifi"/"checkout").
    return t.length >= 4 && hayc.includes(t);
  };

  // Apply search filter across all fields plus category label.
  const search = query.trim().toLowerCase();
  const searchHits = useMemo(() => {
    if (!search) return [];
    const terms = expandQuery(search);
    if (!terms.length) return [];
    return faqs.filter((f) => {
      const catLabel = (CATEGORY_META[f.category] && CATEGORY_META[f.category].label) || f.category || "";
      const raw = `${f.q} ${f.a} ${catLabel}`;
      const hay = normalize(raw);
      const hayc = compact(raw);
      return terms.some((t) => matchTerm(t, hay, hayc));
    });
  }, [faqs, search]);

  const isSearching = search.length > 0;
  const totalVisible = isSearching
    ? searchHits.length
    : activeCategory
      ? (groupedAll[activeCategory] || []).length
      : faqs.length;

  // Which categories to render (filtered to active category if selected & not searching).
  const renderedCategories = useMemo(() => {
    if (isSearching) return [];
    if (activeCategory) return [activeCategory];
    return categoryOrder;
  }, [isSearching, activeCategory, categoryOrder]);

  const scrollToCat = (catKey) => {
    setActiveCategory(null);
    setQuery("");
    // wait one tick for state -> re-render, then scroll
    setTimeout(() => {
      const el = document.getElementById(`cat-${catKey}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const canonical = "https://westbridgehotels.com/faq";
  useSeo({
    title: "FAQ — Westbridge Inn & Suites, Centerville, Iowa",
    description:
      "100+ real answers about staying at Westbridge Inn & Suites in Centerville, Iowa — booking, check-in, pool, pets, groups, and more. Search or browse by topic.",
    canonical,
    jsonLd: [
      buildBreadcrumbLd([{ label: "FAQ", to: "/faq" }]),
      // Match structured data to what's visible on the page: popular + all grouped Q&As
      buildFaqLd([...popular, ...faqs].slice(0, 60)),
    ],
  });

  return (
    <main id="main" data-testid="faq-page">
      <PageHero
        eyebrow="Every question, answered"
        title="Frequently asked."
        intro={`${faqs.length}+ real answers about check-in, parking, pool, pets, groups, and everything else. Search below or browse by topic.`}
        breadcrumbs={[{ label: "FAQ", to: "/faq" }]}
        testId="faq-hero"
        compact
      />

      {/* Sticky search bar */}
      <section
        className="bg-cream/95 backdrop-blur-md py-4 md:py-5 sticky top-16 md:top-20 z-30 border-b border-tan/70"
        data-testid="faq-search-bar"
      >
        <div className="container-app">
          <div className="relative max-w-3xl mx-auto">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/50" />
            <input
              ref={searchInputRef}
              type="search"
              placeholder="Search 100+ questions — pool hours, parking, check-in, pets…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-11 pr-24 py-3 border border-tan bg-cream-50 text-forest-900 placeholder:text-ink/50 focus:outline-none focus:border-brass"
              data-testid="faq-search-input"
              aria-label="Search FAQs"
            />
            {query ? (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink/60 hover:text-forest inline-flex items-center gap-1.5"
                aria-label="Clear search"
                data-testid="faq-search-clear"
              >
                <span className="text-xs uppercase tracking-[0.1em]">Clear</span>
                <X size={16} />
              </button>
            ) : null}
          </div>
          {isSearching && (
            <p className="text-center mt-3 text-sm text-ink/70" data-testid="faq-search-count">
              {searchHits.length} {searchHits.length === 1 ? "match" : "matches"} for "{query}"
            </p>
          )}
        </div>
      </section>

      {/* Popular questions strip — hidden while searching */}
      {!isSearching && popular.length > 0 && (
        <section className="bg-forest-900 text-cream py-14 md:py-20" data-testid="faq-popular-strip">
          <div className="container-app">
            <div className="flex items-center gap-3 mb-8">
              <Star size={18} className="text-brass-300" strokeWidth={1.5} />
              <p className="eyebrow text-brass-300">Most asked</p>
            </div>
            <h2 className="font-display font-light text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-3xl mb-10 md:mb-14">
              The six questions the front desk hears most.
            </h2>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
              {popular.map((f, i) => (
                <details
                  key={i}
                  className="group border-b border-forest-700/70 pb-5"
                  data-testid={`faq-popular-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-start gap-4 outline-none focus-visible:text-brass-300">
                    <span className="text-brass-300 font-mono text-xs mt-1 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="flex-1 font-display text-lg md:text-xl leading-snug">
                      {f.q}
                    </span>
                    <ChevronDown
                      size={18}
                      className="mt-1 text-cream/60 group-open:rotate-180 transition-transform shrink-0"
                    />
                  </summary>
                  <p className="mt-3 ml-8 text-cream/80 text-[0.95rem] leading-relaxed">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Main content: sidebar + grouped categories (or search results) */}
      <section className="bg-cream py-14 md:py-20" data-testid="faq-body">
        <div className="container-app grid md:grid-cols-12 gap-8 md:gap-12">
          {/* Sidebar (sticky on desktop) / horizontal chips (mobile) */}
          <aside className="md:col-span-3" data-testid="faq-sidebar">
            <div className="md:sticky md:top-40">
              <p className="eyebrow mb-4 hidden md:block">Browse by topic</p>
              {/* Mobile chip row */}
              <div className="md:hidden flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 pb-3">
                <button
                  onClick={() => { setActiveCategory(null); setQuery(""); }}
                  className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.12em] whitespace-nowrap ${activeCategory === null && !isSearching ? "bg-forest text-cream" : "border border-forest/30 text-forest-900"}`}
                  data-testid="faq-chip-all"
                >
                  All · {faqs.length}
                </button>
                {categoryOrder.map((c) => {
                  const meta = CATEGORY_META[c] || { label: c };
                  const n = (groupedAll[c] || []).length;
                  return (
                    <button
                      key={c}
                      onClick={() => { setActiveCategory(c); setQuery(""); scrollToCat(c); }}
                      className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.12em] whitespace-nowrap ${activeCategory === c ? "bg-forest text-cream" : "border border-forest/30 text-forest-900"}`}
                      data-testid={`faq-chip-${c}`}
                    >
                      {meta.label} · {n}
                    </button>
                  );
                })}
              </div>
              {/* Desktop sidebar list */}
              <nav aria-label="FAQ categories" className="hidden md:flex flex-col gap-1">
                <button
                  onClick={() => { setActiveCategory(null); setQuery(""); window.scrollTo({top: 0, behavior:'smooth'}); }}
                  className={`text-left py-2 px-3 text-sm border-l-2 transition-colors ${activeCategory === null && !isSearching ? "border-brass text-forest-900 font-medium" : "border-transparent text-ink/70 hover:text-forest-900 hover:border-tan"}`}
                  data-testid="faq-sidebar-all"
                >
                  All questions <span className="text-ink/50">· {faqs.length}</span>
                </button>
                {categoryOrder.map((c) => {
                  const meta = CATEGORY_META[c] || { label: c, icon: HelpCircle };
                  const Icon = meta.icon;
                  const n = (groupedAll[c] || []).length;
                  const active = activeCategory === c;
                  return (
                    <button
                      key={c}
                      onClick={() => scrollToCat(c)}
                      className={`text-left py-2 px-3 text-sm border-l-2 transition-colors flex items-center gap-2 ${active ? "border-brass text-forest-900 font-medium" : "border-transparent text-ink/70 hover:text-forest-900 hover:border-tan"}`}
                      data-testid={`faq-sidebar-${c}`}
                    >
                      <Icon size={14} strokeWidth={1.5} className="text-ink/50 shrink-0" />
                      <span className="flex-1">{meta.label}</span>
                      <span className="text-ink/50">{n}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Results column */}
          <div className="md:col-span-9" data-testid="faq-results">
            {isSearching ? (
              searchHits.length === 0 ? (
                <div className="py-20 text-center border border-tan/60 bg-cream-50" data-testid="faq-no-matches">
                  <p className="eyebrow text-brass mb-3">No matches</p>
                  <p className="text-ink/75 mb-5">
                    Nothing for "{query}". Try a shorter word — or call{" "}
                    <a href="tel:+16414377272" className="brass-underline text-forest-900">
                      (641) 437-7272
                    </a>{" "}
                    for a real answer from the front desk.
                  </p>
                  <button
                    onClick={() => { setQuery(""); searchInputRef.current?.focus(); }}
                    className="text-sm text-forest-900 brass-underline"
                    data-testid="faq-clear-search-cta"
                  >
                    Clear search and browse by topic
                  </button>
                </div>
              ) : (
                <div>
                  <h2 className="font-display text-xl md:text-2xl text-forest-900 mb-6">
                    Search results
                  </h2>
                  <div className="bg-cream-50 border border-tan/60 px-4 md:px-5">
                    {searchHits.length === 1 ? (
                      <FaqRow faq={searchHits[0]} testId="faq-search-0" />
                    ) : (
                      <div className="grid md:grid-cols-2 md:gap-x-8 items-start">
                        <div className="md:border-r md:border-tan/50 md:pr-2">
                          {searchHits.filter((_, i) => i % 2 === 0).map((f, i) => (
                            <FaqRow key={`search-l-${i}`} faq={f} testId={`faq-search-l-${i}`} />
                          ))}
                        </div>
                        <div className="md:pl-2">
                          {searchHits.filter((_, i) => i % 2 === 1).map((f, i) => (
                            <FaqRow key={`search-r-${i}`} faq={f} testId={`faq-search-r-${i}`} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            ) : (
              <div className="space-y-10 md:space-y-12">
                {renderedCategories.map((catKey) => {
                  const list = groupedAll[catKey] || [];
                  if (!list.length) return null;
                  return (
                    <CategorySection
                      key={catKey}
                      catKey={catKey}
                      faqs={list}
                      expandAll={!!activeCategory}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <CtaBlock
        eyebrow="Still have a question?"
        title="The front desk answers 24/7."
        body="No form, no chatbot. Call (641) 437-7272 and a real person picks up."
      />
    </main>
  );
}
